/**
 * F 146 B — the `credential` entity.
 *
 * A credential is an ENTITY, not a row in somebody else's table: it has an
 * id, a canonical `fcid://` reference, timestamps, and it participates in the
 * same taxonomy as every other kind.
 *
 * The one thing it never has is a plaintext field. `encryptedValue` is
 * ciphertext. There is no `value?: string` anywhere on this interface, on
 * purpose — a type that cannot hold the secret cannot leak it, and every
 * accidental `JSON.stringify(credential)` is safe by construction.
 */

import type { CredentialKind, CredentialProvider, CredentialScope, Principal } from '../../core/credentials/credentialStore';

export type CredentialEntityKind = 'api_key' | 'oauth_token' | 'basic_auth' | 'signing_key';

export interface Credential {
  id: string;
  /** Canonical reference, per the FCr convention. */
  fcid: string;
  name: string;
  label: string;
  provider: CredentialProvider;
  kind: CredentialKind;
  /** Whose credential this is. */
  principal: Principal;
  scope: CredentialScope;
  /** AES-GCM ciphertext. NOT the secret. */
  encryptedValue: string;
  iv: string;
  salt: string;
  algorithm: string;
  /** F 145 D — the key schedule that sealed it. */
  kdf: 'argon2id' | 'pbkdf2';
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export const CREDENTIAL_FCID_PREFIX = 'fcid://credential/';

export function credentialFcid(id: string): string {
  return `${CREDENTIAL_FCID_PREFIX}${id}`;
}

export function isCredential(value: unknown): value is Credential {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as Credential).fcid?.startsWith(CREDENTIAL_FCID_PREFIX) === true
  );
}
